// Signal de módulo (per-tab, en memoria) para SUNSET: el aviso de deprecación de la versión vieja
// (v1) + la encuesta de transición. Mismo patrón que permanenceSurvey.ts (un signal desacoplado del
// ciclo de vida del componente que lo dispara), con un añadido: guarda la `sunset_date` por feature.
//
// POR QUÉ vive aquí y NO en el cache de versión (useFeatureVersion/toEntry): el backend emite
// `sunset_date` en el GET /v1/feature-version SOLO a los comercios que resuelven a CONTROL (v1) de un
// experimento con retiro programado. Es un dato DERIVADO y efímero (se re-hidrata en cada GET); meterlo
// en el cache obligaría a cablearlo por toEntry/optimistic/writeCache/readCache (5+ call-sites,
// propenso a romper el read-path). En su lugar: el GET lo setea aquí, el banner/overlay lo leen vía
// useSyncExternalStore, y se vacía al cambiar de sesión/sucursal (resetTransitionSurveyState, llamado
// desde AuthContext junto a los demás resets del módulo).
//
// Disparo de la encuesta: PASIVO, una sola vez por (comercio, feature) con guard durable — al resolver
// `sunset_date` el hook pide la encuesta de transición (omitible, fire-and-forget). El feedback NUNCA
// alimenta el KPI/decisión del experimento (viaja como behavior 'usage_feedback', igual que la encuesta
// de permanencia).

type Listener = () => void;

// sunset_date por feature ('YYYY-MM-DD' o null). null = sin retiro programado (caso normal).
const sunsetByFeature = new Map<string, string | null>();

// true = hay una encuesta de transición pendiente de mostrar para esta feature.
const requested = new Map<string, boolean>();
const listeners = new Map<string, Set<Listener>>();

// Guard DURABLE del disparador PASIVO, keyed por (comercio, feature): la encuesta se abre a lo sumo
// una vez por sesión, sobreviviendo a remounts del shell. Mismo patrón que passiveShownFor de permanence.
const passiveShownFor = new Set<string>();

function getListeners(featureKey: string): Set<Listener> {
  let set = listeners.get(featureKey);
  if (!set) {
    set = new Set();
    listeners.set(featureKey, set);
  }
  return set;
}

function notify(featureKey: string): void {
  getListeners(featureKey).forEach((l) => l());
}

// Suscripción única por feature: cualquier cambio (sunset_date o encuesta pedida) notifica. El
// consumidor combina este subscribe con el getter que necesite (getSunsetDate / isTransitionSurveyRequested).
export function subscribeTransitionSurvey(featureKey: string, listener: Listener): () => void {
  const set = getListeners(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

// ── sunset_date ──────────────────────────────────────────────────────────────────────────────
export function getSunsetDate(featureKey: string): string | null {
  return sunsetByFeature.get(featureKey) ?? null;
}

// Lo setea el GET /v1/feature-version. Idempotente: no re-notifica si la fecha no cambió.
export function setSunsetDate(featureKey: string, date: string | null): void {
  const prev = sunsetByFeature.get(featureKey) ?? null;
  const nextVal = date ?? null;
  if (prev === nextVal) return;
  sunsetByFeature.set(featureKey, nextVal);
  notify(featureKey);
}

// ── encuesta de transición ───────────────────────────────────────────────────────────────────
export function isTransitionSurveyRequested(featureKey: string): boolean {
  return requested.get(featureKey) === true;
}

// Pide mostrar la encuesta (omitible, fire-and-forget). Idempotente.
export function requestTransitionSurvey(featureKey: string): void {
  if (requested.get(featureKey) === true) return;
  requested.set(featureKey, true);
  notify(featureKey);
}

// El shell lo llama al enviar el motivo, omitir/cerrar la encuesta, u optar por no volver.
export function clearTransitionSurvey(featureKey: string): void {
  if (requested.get(featureKey) !== true) return;
  requested.set(featureKey, false);
  notify(featureKey);
}

// Disparador PASIVO con guard durable. Devuelve true si DISPARÓ la encuesta en esta llamada (primera
// vez para ese comercio+feature); false si ya se mostró antes. Sin commerce activo no dispara ni marca
// el guard. Lo invoca el hook al detectar `sunset_date` tras la hidratación.
export function maybeRequestTransitionSurveyForSunset(featureKey: string, commerceId: string | null): boolean {
  if (!commerceId) return false;
  const key = `${commerceId}:${featureKey}`;
  if (passiveShownFor.has(key)) return false;
  passiveShownFor.add(key);
  requestTransitionSurvey(featureKey);
  return true;
}

// Reset de PRODUCCIÓN: al cambiar de sesión/sucursal ni la fecha de retiro, ni un prompt pendiente, ni
// el guard pasivo del comercio anterior deben sobrevivir. Coherente con resetPermanenceSurveyState y el
// resto de resets del módulo (los llama AuthContext en el cambio de sesión/comercio).
export function resetTransitionSurveyState(): void {
  const keys = new Set<string>([...sunsetByFeature.keys(), ...requested.keys()]);
  sunsetByFeature.clear();
  requested.clear();
  passiveShownFor.clear();
  keys.forEach((k) => notify(k));
}

// Solo para tests: limpia el estado de módulo entre casos.
export function __resetTransitionSurveyForTests(): void {
  sunsetByFeature.clear();
  requested.clear();
  listeners.clear();
  passiveShownFor.clear();
}
